import { MEMBERSHIP_RATE_DISPLAY_PERIODS } from 'src/models/circuit/Circuit'
import MembershipInterval from 'src/models/membership/MembershipInterval'
import Model, { encoders, executeMutation, GqlError, ID, Int, Integer, IsoDate, IsoDateTime, JsonArray, MODEL_LEVEL } from 'src/models/model'
import Order from 'src/models/order/Order'
import Site from 'src/models/site/Site'
import User, { USER_TYPES } from 'src/models/user/User'

export const MEMBERSHIP_PERIOD_STRATEGIES = {
  PRORATE: 'prorate',
  REPLACE: 'replace',
  APPEND: 'append'
}

export default class Membership extends Model {
  static name = 'Membership'

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static isExportable = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer]
  static viewAction = 'ViewMemberships'
  static manageAction = 'ManageMemberships'

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      members: {
        type: [User],
        output: true,
        reference: true
      },
      currentMembershipInterval: {
        type: MembershipInterval,
        output: true
      },
      membershipIntervals: {
        type: [MembershipInterval],
        output: true,
        reference: true
      },
      state: {
        type: String,
        output: true
      },
      term: {
        type: Integer,
        default: 1, // Default to monthly
        output: true
      },
      owner: {
        type: User,
        reference: true,
        encode: encoders.hasOne
      },
      site: {
        type: Site,
        local: true
      },
      pendingOrderId: {
        type: ID,
        default: null,
        excludeFromDefault: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },
      canceledAt: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      cancelDescription: {
        type: String,
        default: null,
        output: true
      }
    }

    this.mutations = {
      createMembershipOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          members: {
            type: JsonArray,
            required: true
          },
          term: {
            type: Int,
            required: true
          },
          periodStrategy: {
            type: String,
            required: false
          },
          adminEndDate: {
            type: String,
            required: false
          }
        },
        results: {
          order: Order,
          errors: [GqlError]
        }
      },
      createChangeBillingDayOrder: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          billingDayOfMonth: {
            type: Int,
            required: true
          }
        },
        results: {
          order: Order,
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
  }

  get effective () {
    return this.state === 'active'
  }

  get isRenewing () {
    return this.effective && !this.canceledAt
  }

  // Initial member is highest priced member
  get initialMember () {
    return _.sortBy(this.members.data, member => {
      if (!member.membershipType) return 1

      if (member.membershipType.free) return 1

      if (!member.membershipUserType) return 1

      return -member.membershipUserType.monthlyRate
    })[0]
  }

  get hasGuests () {
    return _.some(this.members.data, { type: USER_TYPES.GUEST })
  }

  get monthlyRate () {
    const rate = _(this.members?.data).map(member => {
      return member.getMembershipMonthlyRate(this.term)
    }).sum()

    return rate || 0
  }

  get rate () {
    return this.monthlyRate * this.term || 0
  }

  get discountPercent () {
    if (!this.configuration.currentCircuit) return 0 // throw new Error("Site not loaded, can't determine discount percent")

    switch (this.term) {
      case 1:
        return 0
      case 3:
        return this.configuration.currentCircuit.quarterlyDiscountPercentThousandths / 1000
      case 6:
        return this.configuration.currentCircuit.biannualDiscountPercentThousandths / 1000
      case 12:
        return this.configuration.currentCircuit.annualDiscountPercentThousandths / 1000
      default:
        throw new Error('Unhandled term length: ' + this.term)
    }
  }

  get termName () {
    switch (this.term) {
      case 1:
        return this.i18n.t('time.monthly')
      case 3:
        return this.i18n.t('time.quarterly')
      case 6:
        return this.i18n.t('time.biannually')
      case 12:
        return this.i18n.t('time.annually')
      default:
        throw new Error('Unhandled term length: ' + this.term)
    }
  }

  get ticketRewardDescription () {
    const membershipType = this.initialMember.membershipType
    if (membershipType.hasYearlyTicketVouchers && this.term === 12) {
      return `${membershipType.yearlyTicketVoucherCount} Ticket Vouchers for ${membershipType.voucherPerMember ? 'Each Member' : 'Membership Owner'}`
    } else if (membershipType.hasMonthlyTicketVouchers) {
      return `${membershipType.monthlyTicketVoucherCount * this.term} Ticket Vouchers for ${membershipType.voucherPerMember ? 'Each Member' : 'Membership Owner'}`
    } else return null
  }

  get detailDescription () {
    return _.map(this.members.data, member => {
      let rate = member.getMembershipMonthlyRate(this.term) * this.configuration.currentCircuit.membershipRateDisplayPeriod

      if (rate === 0) {
        rate = 'Free'
      } else {
        rate = this.i18n.n(rate, 'currency')
      }

      const userData = {
        type: member.type,
        first_name: member.firstName,
        last_name: member.lastName,
        email: member.email,
        phone: member.phone,
        date_of_birth: member.dateOfBirth,
        membership_type_id: member.membershipType.id,
        user_class_id: member.userClass.id
      }

      if (member.isRemote) {
        userData.id = member.id
      }

      return {
        key: `${member.id} - ${member.name} - ${member.membershipType.name} (${member.userClass.name} - ${rate})`,
        description: `${member.name} - ${member.membershipType.name} (${member.userClass.name} - ${rate} / ${this.configuration.currentCircuit.membershipRateDisplayPeriod === MEMBERSHIP_RATE_DISPLAY_PERIODS.MONTHLY ? 'monthly' : 'yearly'})`,
        user_data: userData
      }
    })
  }

  get hasAnyUserWithPaymentInfo () {
    return _.some(this.members.data, member => member.defaultPaymentMethodId)
  }

  get canHaveOwnerWithoutPaymentInfo () {
    return !this.owner && !this.hasAnyUserWithPaymentInfo
  }

  memberData () {
    return _.map(this.members.data, member => {
      const userData = {
        type: member.type,
        first_name: member.firstName,
        last_name: member.lastName,
        email: member.email,
        phone: member.phone,
        date_of_birth: member.dateOfBirth,
        membership_type_id: member.membershipType.id,
        user_class_id: member.userClass.id,
        receive_marketing_emails: member.receiveMarketingEmails
      }

      if (member.isRemote) {
        userData.id = member.id
      }

      return userData
    })
  }

  async createMembershipOrder (periodStrategy, adminEndDate) {
    const results = await executeMutation(
      'createMembershipOrder',
      {
        id: this.id,
        members: JSON.stringify(this.memberData() || []),
        term: this.term,
        periodStrategy,
        adminEndDate: IsoDate.encode(adminEndDate)
      },
      {
        order: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return {
      errors: results.errors,
      order: results.data?.order
    }
  }

  async createChangeBillingDayOrder (billingDayOfMonth) {
    const results = await executeMutation(
      'createChangeBillingDayOrder',
      {
        id: this.id,
        billingDayOfMonth
      },
      {
        order: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return {
      errors: results.errors,
      order: results.data?.order
    }
  }
}
